import { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { useTranslate } from "@similarweb/i18n-provider";
import type { IconName } from "@similarweb/icons";
import { IconButton, Tooltip } from "@similarweb/ui-components";

import type { ICategory, ICustomCategory } from "app/common/services/categoryService.types";
import { QueryBarItem } from "app/components/compare/QueryBarItem";

import { shouldEnableCompareButton } from "../../routes/routeUtils";
import { CategorySearchContainer } from "../styles";
import type { ICategoryModalProps } from "../types";

import { IndustryAnalysisSearch } from "./IndustryAnalysisSearch";

interface ICompareButtonProps {
    showOnlyCategories?: boolean;
    showOnlyCustom?: boolean;
    placeholder?: string;
    categoryModalProps?: ICategoryModalProps;
    isPartnerPage?: boolean;
    customResolveItemIcon?: (item: ICategory, isChild: boolean) => IconName;
    selectedCategory: ICategory | ICustomCategory | null;
    onCompareCategorySelect: (category: ICategory) => void;
    disabledCategories?: (ICategory | ICustomCategory)[];
    hasSelectedCategoryWithSegments?: boolean;
}

export const CompareButton = ({
    showOnlyCategories,
    showOnlyCustom,
    placeholder,
    categoryModalProps,
    isPartnerPage,
    customResolveItemIcon,
    selectedCategory,
    onCompareCategorySelect,
    disabledCategories = [],
    hasSelectedCategoryWithSegments,
}: ICompareButtonProps) => {
    const { translate } = useTranslate();
    const { state } = useCurrentStateAndParams();
    const [isCompareSearchOpen, setIsCompareSearchOpen] = useState(false);
    const isComparisonEnabled = shouldEnableCompareButton(state.name);

    const handleCompareClick = () => {
        setIsCompareSearchOpen(true);
    };

    const handleCompareSearchClose = () => {
        setIsCompareSearchOpen(false);
    };

    const handleCompareCategorySelect = (category: ICategory) => {
        onCompareCategorySelect(category);
        setIsCompareSearchOpen(false);
    };

    const isDisabled = !isComparisonEnabled;

    const getTooltipContent = () => {
        if (!isComparisonEnabled) {
            return translate("industry.overview.query.bar.compare.button.route.not.supported", {
                pageTitle: translate(state.pageTitle as string),
            });
        }
        return null;
    };

    const compareButtonComponent = (
        <Tooltip
            content={getTooltipContent()}
            placement="bottom"
        >
            <IconButton
                className="QueryBar-compare-button"
                onClick={handleCompareClick}
                iconName="add"
                isDisabled={isDisabled}
            >
                {translate("keyword.query.bar.compare.button.text")}
            </IconButton>
        </Tooltip>
    );

    const compareSearchComponent = (
        <CategorySearchContainer>
            <IndustryAnalysisSearch
                onItemClick={handleCompareCategorySelect}
                onClose={handleCompareSearchClose}
                showOnlyCategories={showOnlyCategories}
                showOnlyCustom={showOnlyCustom}
                placeholder={placeholder || translate("keyword.query.bar.compare.search.placeholder")}
                categoryModalProps={categoryModalProps}
                isPartnerPage={isPartnerPage}
                customResolveItemIcon={customResolveItemIcon}
                selectedCategory={selectedCategory}
                disabledCategories={disabledCategories}
                isCompare={true}
                hasSelectedCategoryWithSegments={hasSelectedCategoryWithSegments}
            />
        </CategorySearchContainer>
    );

    return (
        <QueryBarItem
            id="industry-analysis-query-bar-compare"
            isLoading={false}
            activeItem={isCompareSearchOpen ? "industry-analysis-query-bar-compare" : null}
            renderComponent={compareButtonComponent}
            searchComponent={compareSearchComponent}
        />
    );
};
